import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import FaKnowledgeUploadModal from './FaKnowledgeUploadModal';
import './FAKnowledgePage.css';

const CATEGORY_COLORS = {
  'Backgrounds': '#2563eb',
  'Drug Testing': '#7c3aed',
  'Fingerprinting': '#0891b2',
  'I9': '#b45309',
  'Income & Wage Verification': '#065f46',
  'Resident Screening': '#9f1239',
  'Scheduling': '#1d4ed8',
  'Unknown': '#6b7280',
};

export default function FAKnowledgePage({ user, onLogout }) {
  const navigate = useNavigate();
  const isAdmin = user.role?.toLowerCase() === 'admin';
  const chatEndRef = useRef(null);

  const [entries, setEntries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! Ask me anything about First Advantage issues — I\'ll answer based on past support transcripts uploaded here.' }
  ]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const loadEntries = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/fa-knowledge');
      setEntries(data);
    } catch {}
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(data);
    } catch {}
  }, []);

  useEffect(() => { loadEntries(); loadCompanies(); }, [loadEntries, loadCompanies]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleUploaded = () => {
    setUploadMsg({ type: 'success', text: 'Transcript processed and added to knowledge base.' });
    loadEntries();
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Remove this transcript from the knowledge base?')) return;
    try {
      await axios.delete(`/api/fa-knowledge/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch {}
  };

  const sendMessage = async () => {
    if (!input.trim() || chatLoading) return;
    const question = input.trim();
    setInput('');
    const history = messages.slice(1).map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setChatLoading(true);
    try {
      const { data } = await axios.post('/api/fa-knowledge/chat', { question, history });
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setChatLoading(false);
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  return (
    <div className="fak-page">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="fak-body">

        {/* Left Panel — Transcripts */}
        <div className="fak-left">
          <div className="fak-left-top">
            <h2>Transcripts <span className="fak-count">{entries.length}</span></h2>
            <button className="fak-upload-btn" onClick={() => { setUploadMsg(null); setShowUploadModal(true); }}>
              + Upload PDF
            </button>
          </div>

          {uploadMsg && (
            <div className={`fak-msg ${uploadMsg.type}`}>{uploadMsg.text}</div>
          )}

          {entries.length === 0 && (
            <p className="fak-empty">No transcripts uploaded yet. Upload a First Advantage chat PDF to get started.</p>
          )}

          <div className="fak-entries">
            {entries.map(e => (
              <div key={e.id} className="fak-entry-card">
                <div className="fak-entry-top">
                  <span className="fak-cat-badge" style={{ background: CATEGORY_COLORS[e.category] || '#6b7280' }}>
                    {e.category || 'Unknown'}
                  </span>
                  <span className="fak-entry-date">{e.chat_date ? new Date(e.chat_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                  {isAdmin && (
                    <button className="fak-del-btn" onClick={() => deleteEntry(e.id)} title="Remove">✕</button>
                  )}
                </div>
                {e.candidate_name && <p className="fak-candidate">Re: {e.candidate_name}{e.company_name ? ` · ${e.company_name}` : ''}</p>}
                <p className="fak-issue"><strong>Issue:</strong> {e.issue_summary}</p>
                <p className="fak-resolution"><strong>Resolution:</strong> {e.resolution}</p>
                <p className="fak-meta">Uploaded by {e.uploaded_by_name || 'Unknown'} · {e.recruiter_name ? `Recruiter: ${e.recruiter_name}` : ''}</p>
              </div>
            ))}
          </div>
        </div>

        {showUploadModal && (
          <FaKnowledgeUploadModal
            companies={companies}
            onClose={() => setShowUploadModal(false)}
            onUploaded={handleUploaded}
          />
        )}

        {/* Right Panel — Chat */}
        <div className="fak-right">
          <div className="fak-chat-header">
            <h2>Ask the Knowledge Base</h2>
            <p className="fak-chat-subtitle">Powered by {entries.length} transcript{entries.length !== 1 ? 's' : ''}</p>
          </div>

          <div className="fak-messages">
            {messages.map((m, i) => (
              <div key={i} className={`fak-msg-bubble ${m.role}`}>
                <div className="fak-bubble-label">{m.role === 'assistant' ? 'FA Knowledge' : 'You'}</div>
                <div className="fak-bubble-text">{m.content}</div>
              </div>
            ))}
            {chatLoading && (
              <div className="fak-msg-bubble assistant">
                <div className="fak-bubble-label">FA Knowledge</div>
                <div className="fak-typing"><span /><span /><span /></div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="fak-input-row">
            <textarea
              className="fak-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask a question about FA issues… (Enter to send)"
              rows={2}
              disabled={chatLoading}
            />
            <button className="fak-send-btn" onClick={sendMessage} disabled={chatLoading || !input.trim()}>
              Send
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
